import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import Radio from '@groovehq/internal-design-system/lib/components/Radio/Radio'
import { useBilling } from 'ducks/billing/hooks'
import { useAnnualOfferFigures } from 'ducks/billing/useAnnualOfferFigures'
import { selectAnnualOffer } from 'ducks/billing/selectors/selectAnnualOffer'
import { selectIsPendingAnnualSwitch } from 'ducks/billing/selectors/selectIsPendingAnnualSwitch'
import { selectUpgradeBreakdownForPricingIds } from 'ducks/billing/selectors/selectUpgradeBreakdownForPricingIds'
import { getFormattedPrice } from 'util/numbers'
import { strftime } from 'util/date'
import { AdminAccessDrawer } from '../../NoAccess'
import { styles } from './AnnualCycleSwitchDetails.styles'

const sources = [
  'billing_offer',
  'summary_link',
  'reminder',
  'seat_add',
  'trial',
  'bf_billing_offer',
  'bf_reminder',
  'bf_email',
]
const normalizeTiming = value => (value === 'now' ? 'now' : 'renewal')
const formatDate = (format, seconds) =>
  strftime(format, seconds * 1000).replace(/\b0(\d)\b/g, '$1')

const AnnualCycleSwitchDetails = ({
  open,
  onExit,
  drawerAnnualTiming,
  drawerAnnualSource,
}) => {
  const {
    currentPricingIds,
    plans,
    updateSubscription,
    billingCycle,
    isSaving,
  } = useBilling()
  const figures = useAnnualOfferFigures()
  const offer = useSelector(selectAnnualOffer)
  const pending = useSelector(selectIsPendingAnnualSwitch)
  const [upgradeAt] = useState(() => Date.now())
  const breakdown = useSelector(state =>
    selectUpgradeBreakdownForPricingIds(
      state,
      currentPricingIds,
      currentPricingIds,
      'monthly',
      'annual',
      upgradeAt,
      Object.fromEntries(
        plans
          .filter(plan => Number.isFinite(plan.quantity))
          .map(plan => [plan.pricing.id, plan.quantity])
      )
    )
  )
  const [timing, setTiming] = useState(() =>
    normalizeTiming(drawerAnnualTiming)
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submitting = useRef(false)
  const promotion = !!offer?.promotion
  useEffect(
    () => {
      setTiming(promotion ? 'renewal' : normalizeTiming(drawerAnnualTiming))
    },
    [drawerAnnualTiming, promotion]
  )
  const selectTiming = event => setTiming(normalizeTiming(event.target.value))
  const selectedTiming = promotion ? 'renewal' : timing
  const {
    currency,
    nextBillDate,
    planName,
    seats,
    isSeatBased,
    annualTotal,
    monthlyTotal,
    annualPerMonth,
    yearlySaving,
    monthlySeatPrice,
    annualSeatPrice,
  } = figures
  const valid =
    offer?.surfaces?.v2Enabled === true &&
    (offer.eligible === true || pending) &&
    billingCycle === 'monthly' &&
    currentPricingIds.length > 0 &&
    figures.ready &&
    yearlySaving > 0 &&
    !!currency &&
    Number.isFinite(nextBillDate) &&
    nextBillDate > 0 &&
    Number.isInteger(breakdown.prorataCharge) &&
    Number.isInteger(breakdown.prorataRefund)
  const money = amount => getFormattedPrice(Math.round(amount), currency)
  const headline = amount => money(amount).replace(/\.00$/, '')
  const date = valid ? formatDate('%d %b', nextBillDate) : ''
  const renewalDate = valid
    ? `${new Date(nextBillDate * 1000).toLocaleDateString('en-US', {
        weekday: 'long',
      })}, ${formatDate("%d %b '%y", nextBillDate)}`
    : ''
  const dueNow = breakdown.prorataCharge - breakdown.prorataRefund
  const disabled = !valid || isSubmitting || isSaving
  const close = () => {
    if (!submitting.current) onExit()
  }
  const submit = async () => {
    if (!valid || submitting.current || isSaving) return
    submitting.current = true
    setIsSubmitting(true)
    const renewal = selectedTiming === 'renewal'
    try {
      await updateSubscription(
        {
          pricingIds: currentPricingIds,
          cycle: 'annual',
          ...(renewal ? { scheduleChanges: true } : {}),
          ...(sources.includes(drawerAnnualSource)
            ? { source: drawerAnnualSource }
            : {}),
        },
        {
          convertTrials: false,
          successMessage: renewal
            ? `You'll switch to annual billing on ${date}`
            : "You're now on annual billing",
        }
      )
      onExit()
    } catch (error) {
      // The billing operation owns the failure toast; leave the drawer retryable.
    } finally {
      submitting.current = false
      setIsSubmitting(false)
    }
  }
  const suffix = isSeatBased ? ` × ${seats}` : ''
  const footer = (
    <div className="grui" css={styles.actions}>
      <Button
        type="tertiary"
        size="big"
        css={[styles.control, styles.quietButton]}
        onClick={close}
        disabled={isSubmitting}
      >
        Keep monthly
      </Button>
      <Button
        type="info"
        size="big"
        css={[styles.control, styles.primaryButton]}
        onClick={submit}
        disabled={disabled}
      >
        {selectedTiming === 'renewal'
          ? `Confirm switch on ${date}`
          : 'Switch now'}
      </Button>
    </div>
  )
  return (
    <AdminAccessDrawer
      css={styles.drawer}
      title="Switch to annual billing"
      open={open}
      onClose={close}
      footer={footer}
    >
      {valid && (
        <div className="grui" css={styles.body}>
          <section className="grui" css={styles.summary}>
            <div className="grui">{"You'll save"}</div>
            <strong className="grui">{headline(yearlySaving)} a year</strong>
            <p className="grui">
              {isSeatBased
                ? `${planName} · ${seats} seats · ${headline(
                    monthlySeatPrice
                  )} → ${headline(annualSeatPrice)} per user / month`
                : `${planName} · ${headline(monthlyTotal)} → ${headline(
                    annualPerMonth
                  )} / month`}
            </p>
          </section>
          <fieldset
            className="grui"
            css={styles.choices}
            disabled={isSubmitting}
          >
            <legend className="grui">When should it start?</legend>
            <Radio
              id="annual-renewal"
              name="annual-timing"
              value="renewal"
              checked={selectedTiming === 'renewal'}
              onChange={selectTiming}
              className="grui"
              css={[
                styles.option,
                selectedTiming === 'renewal' && styles.selectedOption,
              ]}
            >
              <span className="grui" css={styles.optionHeading}>
                <strong className="grui">When my plan renews</strong>
                <span className="grui" css={styles.chip}>
                  Recommended
                </span>
              </span>
              <span className="grui" css={styles.description}>
                {renewalDate}. Nothing is charged today; your next invoice is{' '}
                {headline(annualTotal)} for the year.
              </span>
            </Radio>
            {!promotion && (
              <Radio
                id="annual-now"
                name="annual-timing"
                value="now"
                checked={selectedTiming === 'now'}
                onChange={selectTiming}
                className="grui"
                css={[
                  styles.option,
                  selectedTiming === 'now' && styles.selectedOption,
                ]}
              >
                <strong className="grui">Right away</strong>
                <span className="grui" css={styles.description}>
                  Charged today: {headline(annualTotal)} minus a credit of about{' '}
                  {money(breakdown.prorataRefund)} for the{' '}
                  {breakdown.refundDays} unused days of this month ≈{' '}
                  <strong className="grui">{money(dueNow)}</strong>.
                </span>
              </Radio>
            )}
          </fieldset>
          <div className="grui" css={styles.rows}>
            <div className="grui" css={[styles.row, styles.muted]}>
              <span className="grui">
                Now · Monthly {planName}
                {suffix}
              </span>
              <s className="grui">{headline(monthlyTotal)} /mo</s>
            </div>
            <div className="grui" css={styles.row}>
              <span className="grui">
                <strong className="grui">New</strong> · Annual {planName}
                {suffix}
              </span>
              <strong className="grui">{headline(annualTotal)} /yr</strong>
            </div>
            <div className="grui" css={[styles.row, styles.equivalent]}>
              <span className="grui">Works out to</span>
              <span className="grui">{headline(annualPerMonth)} /mo</span>
            </div>
            <div className="grui" css={[styles.row, styles.dueToday]}>
              <strong className="grui">Due today</strong>
              <strong className="grui">
                {money(selectedTiming === 'renewal' ? 0 : dueNow)}
              </strong>
            </div>
          </div>
          <ul className="grui" css={styles.notes}>
            {isSeatBased && (
              <li className="grui">
                Seats you add during the year are prorated onto the annual plan.
              </li>
            )}
            <li className="grui">
              Annual plans renew yearly. Moving back to monthly is done through
              our support team.
            </li>
          </ul>
        </div>
      )}
    </AdminAccessDrawer>
  )
}
export default AnnualCycleSwitchDetails
